import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, Layers, RectangleHorizontal, RectangleVertical, SquareRoundCorner } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { DesignCanvas } from "@/components/designer/DesignCanvas";
import { RoomPreview } from "@/components/designer/RoomPreview";
import { PhotoTray } from "@/components/designer/PhotoTray";
import { CellControls, HistoryButtons, LayoutPicker } from "@/components/designer/Controls";
import { DRAFT_KEY, useDesigner } from "@/hooks/useDesigner";
import { api, errMsg } from "@/lib/api";
import { useCart, useCartActions } from "@/lib/cart";
import { cellPpi, newCell, ppiLevel } from "@/lib/designer";
import { dims, formatCOP, useCatalog, useSeo } from "@/lib/site";

const ORIENTATIONS = ["vertical", "horizontal"];
const choice = (on) => `border transition-colors ${on ? "border-[#080808] bg-[#080808] text-[#FAF8F4]" : "border-[#080808]/15 bg-white hover:border-[#080808]/50"}`;
const H3 = ({ children }) => <h2 className="mb-3 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-[#080808]/80">{children}</h2>;

const SizeButtons = ({ products, value, orientation, onChange }) => (
  <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Tamaño">
    {products.map((p) => {
      const d = dims(p, orientation);
      return (
        <button key={p.slug} type="button" role="radio" aria-checked={p.slug === value} data-testid={`size-${p.slug}`} onClick={() => onChange(p.slug)} className={`py-3 ${choice(p.slug === value)}`}>
          <span className="block font-serif-display text-2xl leading-none">{p.size}</span>
          <span data-testid={`size-${p.slug}-dims`} className="mt-1 block text-[0.7rem] opacity-75">{d.w} × {d.h} cm</span>
        </button>
      );
    })}
  </div>
);

const OrientationButtons = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Orientación">
    {ORIENTATIONS.map((o) => {
      const Icon = o === "vertical" ? RectangleVertical : RectangleHorizontal;
      return (
        <button key={o} type="button" role="radio" aria-checked={value === o} data-testid={`designer-orientation-${o}`} onClick={() => onChange(o)}
          className={`flex items-center justify-center gap-2 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.14em] ${choice(value === o)}`}>
          <Icon className="h-4 w-4" strokeWidth={1.6} /> {o === "vertical" ? "Vertical" : "Horizontal"}
        </button>
      );
    })}
  </div>
);

const ModeToggle = ({ collage, onChange }) => (
  <div className="grid grid-cols-2 rounded-full border border-[#080808]/15 bg-white p-1" role="radiogroup" aria-label="Una foto o collage">
    {[[false, "Una foto"], [true, "Collage"]].map(([v, l]) => (
      <button key={l} type="button" role="radio" aria-checked={collage === v} data-testid={`mode-${v ? "collage" : "single"}`} onClick={() => onChange(v)}
        className={`rounded-full py-2.5 text-[0.72rem] font-semibold uppercase tracking-[0.14em] transition-colors ${collage === v ? "bg-[#080808] text-[#FAF8F4]" : "text-[#080808]/65 hover:text-[#080808]"}`}>{l}</button>
    ))}
  </div>
);

const RoomModeToggle = ({ mode, setMode }) => (
  <div role="radiogroup" aria-label="Modo de vista" className="inline-flex rounded-full border border-[#080808]/15 bg-white p-1">
    {[["detail", "Ver detalle"], ["scale", "Ver a escala"]].map(([k, l]) => (
      <button key={k} type="button" role="radio" aria-checked={mode === k} data-testid={`room-mode-${k}`} onClick={() => setMode(k)}
        className={`rounded-full px-5 py-2 text-sm transition-colors ${mode === k ? "bg-[#080808] text-[#FAF8F4]" : "text-[#080808]/70 hover:text-[#080808]"}`}>{l}</button>
    ))}
  </div>
);

const ViewTabs = ({ view, setView }) => (
  <div role="tablist" className="inline-flex rounded-full border border-[#080808]/15 bg-white p-1">
    {[["room", "Ver en mi espacio"], ["editor", "Editar collage"]].map(([k, l]) => (
      <button key={k} role="tab" aria-selected={view === k} data-testid={`view-tab-${k}`} onClick={() => setView(k)}
        className={`rounded-full px-4 py-2 text-[0.68rem] font-medium uppercase tracking-[0.12em] transition-colors ${view === k ? "bg-[#080808] text-[#FAF8F4]" : "text-[#080808]/65 hover:text-[#080808]"}`}>{l}</button>
    ))}
  </div>
);

const Designer = ({ products, cfg }) => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const talla = products.some((p) => p.slug === params.get("talla")) ? params.get("talla") : null;
  const ori = ORIENTATIONS.includes(params.get("orientacion")) ? params.get("orientacion") : null;
  const editId = params.get("diseno");
  const d = useDesigner({
    slug: talla || (products.find((p) => p.slug === "m") || products[0]).slug, orientation: ori || "vertical",
    designParam: editId, override: { slug: talla, orientation: ori },
  });
  const { state } = d;
  const product = products.find((p) => p.slug === state.slug) || products[0];
  const { data: photoList = [], refetch } = useQuery({ queryKey: ["designer-photos"], queryFn: () => api.get("/designer/photos").then((r) => r.data) });
  const { data: cart } = useCart();
  const { add } = useCartActions();
  const uploadRef = useRef(null);
  const [selected, setSelected] = useState(0);
  const [pending, setPending] = useState(null);
  const [view, setView] = useState("room");
  const [roomMode, setRoomMode] = useState("detail");
  const [hint, setHint] = useState(false);
  const [swapFrom, setSwapFrom] = useState(null);
  const [cropOk, setCropOk] = useState(false);
  const [lowOk, setLowOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const gestureStart = useRef(null);
  useEffect(() => {
    if (d.loaded) navigate(`/personalizar?talla=${state.slug}&orientacion=${state.orientation}${editId ? `&diseno=${editId}` : ""}`, { replace: true });
  }, [state.slug, state.orientation, d.loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setCropOk(false); setLowOk(false); }, [state]);
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 1024px)");
    const fn = (e) => setIsDesktop(e.matches);
    m.addEventListener("change", fn);
    return () => m.removeEventListener("change", fn);
  }, []);

  const photos = useMemo(() => Object.fromEntries(photoList.map((p) => [p.id, p])), [photoList]);
  const { w: wcm, h: hcm } = dims(product, state.orientation);
  const rects = cfg.templates[state.layout][state.orientation];
  const ppis = state.cells.map((c, i) => (photos[c.photo_id] ? cellPpi(photos[c.photo_id], c, rects[i], wcm, hcm) : null));
  const levels = ppis.map((p) => (p == null ? "empty" : ppiLevel(p, cfg)));
  const sel = Math.min(selected, state.cells.length - 1);
  const filled = state.cells.filter((c) => photos[c.photo_id]).length;
  const missing = state.cells.length - filled;
  const hasLow = levels.includes("low");
  const collage = state.layout > 1;
  const inCart = cart?.items.some((i) => i.design_id === d.design?.id);

  const updateCell = (i, patch, commit) => {
    const fn = (cur) => ({ ...cur, cells: cur.cells.map((c, k) => (k === i ? { ...c, ...patch } : c)) });
    commit ? d.commit(fn) : d.preview(fn);
  };
  const onSelectCell = (i) => {
    if (swapFrom != null && swapFrom !== i) {
      d.commit((cur) => { const cells = [...cur.cells]; [cells[swapFrom], cells[i]] = [cells[i], cells[swapFrom]]; return { ...cur, cells }; });
      setSwapFrom(null);
    }
    setSelected(i);
  };
  const openUpload = (i) => { if (i != null) setSelected(i); replaceTarget.current = null; uploadRef.current?.click(); };
  const replaceTarget = useRef(null);
  const adjust = (
    <CellControls index={sel} cell={state.cells[sel]} hasPhoto={!!photos[state.cells[sel]?.photo_id]} ppi={ppis[sel]} level={levels[sel]}
      canSwap={collage} swapping={swapFrom === sel} onSwap={() => setSwapFrom(swapFrom === sel ? null : sel)} onReplace={() => { openUpload(sel); replaceTarget.current = sel; }}
      onChange={(p) => { if (!gestureStart.current) gestureStart.current = state; updateCell(sel, p, false); }}
      onCommitChange={(p) => { if (gestureStart.current) { d.pushHistory(gestureStart.current); gestureStart.current = null; updateCell(sel, p, false); } else updateCell(sel, p, true); }}
      onRemove={() => updateCell(sel, newCell(), true)} />
  );
  const changeShape = (patch) => {
    d.commit((cur) => ({ ...cur, ...patch }));
    if (filled > 0) setHint(true);
  };
  const changeLayout = (l) => {
    if (l === state.layout) return;
    const apply = () => d.commit((cur) => {
      const kept = cur.cells.filter((c) => c.photo_id);
      return { ...cur, layout: l, cells: Array.from({ length: l }, (_, i) => kept[i] || newCell()) };
    });
    setSwapFrom(null);
    if (filled > l) setPending({ apply, lost: filled - l }); else { apply(); if (filled > 0) setHint(true); }
  };
  const pick = (photoId) => {
    updateCell(sel, newCell(photoId), true);
    const nextEmpty = state.cells.findIndex((c, k) => k > sel && !c.photo_id);
    if (nextEmpty >= 0) setSelected(nextEmpty);
  };
  const onUploaded = (p) => {
    refetch();
    const forced = replaceTarget.current;
    replaceTarget.current = null;
    d.commit((cur) => {
      const target = forced != null ? forced : !cur.cells[sel]?.photo_id ? sel : cur.cells.findIndex((c) => !c.photo_id);
      const t = target < 0 && cur.layout === 1 ? 0 : target;
      return t < 0 ? cur : { ...cur, cells: cur.cells.map((c, k) => (k === t ? newCell(p.id) : c)) };
    });
  };
  const confirmAndAdd = async () => {
    if (missing) { setView("editor"); return toast.error(`Faltan ${missing} foto(s): completa los espacios marcados.`); }
    setBusy(true);
    try {
      let des = await d.save(state);
      if (des.status !== "confirmed") {
        des = (await api.post(`/designs/${des.id}/confirm`, { crop_confirmed: cropOk, low_res_confirmed: lowOk })).data;
        d.setDesign(des);
      }
      const res = await add.mutateAsync(des.id);
      if (!editId) localStorage.removeItem(DRAFT_KEY);
      toast.success(res.updated ? "Carrito actualizado" : "Lámina agregada al carrito");
      navigate("/carrito");
    } catch (e) { toast.error(e.message || errMsg(e)); }
    setBusy(false);
  };
  const label = `${product.size} · ${wcm} × ${hcm} cm`;
  const roomProps = { rects, cells: state.cells, photos, orientation: state.orientation };

  return (
    <div className="mx-auto grid max-w-[1680px] grid-cols-1 gap-8 px-5 py-6 sm:px-8 sm:py-10 lg:grid-cols-[minmax(0,65fr)_minmax(0,35fr)] lg:gap-12 lg:px-10">
      <div data-testid="preview-column" className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ViewTabs view={view} setView={setView} />
          <div className="flex items-center gap-4">
            <span data-testid="save-status" className="text-xs text-[#080808]/60">{{ saving: "Guardando…", saved: "Borrador guardado", error: "No se pudo guardar" }[d.saving] || ""}</span>
            <HistoryButtons canUndo={d.canUndo} canRedo={d.canRedo} undo={d.undo} redo={d.redo} />
          </div>
        </div>
        {hint && (
          <div data-testid="reframe-hint" className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-[#B18A4B]/50 bg-[#D9B978]/15 px-4 py-3 text-sm">
            <span>Cambiaste el formato: revisa el encuadre de tus fotos.</span>
            <span className="flex gap-3">
              <button type="button" data-testid="reframe-hint-open" onClick={() => { setView("editor"); setHint(false); }} className="text-[0.7rem] font-semibold uppercase tracking-[0.1em] underline underline-offset-4">Revisar encuadre</button>
              <button type="button" data-testid="reframe-hint-dismiss" onClick={() => setHint(false)} aria-label="Cerrar aviso" className="text-[#080808]/50 hover:text-[#080808]">✕</button>
            </span>
          </div>
        )}
        <div className="mt-4">
          {view === "room" ? (
            <div className="mx-auto lg:max-w-[calc((100svh-210px)*650/480)]">
              <div className="mb-3 flex justify-center"><RoomModeToggle mode={roomMode} setMode={setRoomMode} /></div>
              <RoomPreview mode={roomMode} wcm={wcm} hcm={hcm} {...roomProps} label={label}
                onPlateClick={filled > 0 ? () => setView("editor") : undefined} />
              <p className="mt-2 text-xs text-[#080808]/55">Bordes redondeados, sombra y efecto flotante forman parte de la simulación; no se imprimen.</p>
            </div>
          ) : (
            <div data-testid="collage-editor" className="overflow-hidden bg-white p-4 sm:p-10">
              <DesignCanvas maxH={isDesktop ? 600 : 360} wcm={wcm} hcm={hcm} rects={rects} cells={state.cells} photos={photos} selected={sel} qualities={levels}
                missing={filled > 0 && missing > 0} swapping={swapFrom != null}
                onSelect={onSelectCell} onAdd={openUpload}
                onStart={() => { gestureStart.current = state; }} onLive={(i, patch) => updateCell(i, patch, false)}
                onEnd={() => { if (gestureStart.current) d.pushHistory(gestureStart.current); gestureStart.current = null; }} />
              <p className="mt-4 text-center text-xs leading-relaxed text-[#080808]/60">Toca un espacio para editarlo. La línea punteada es el contorno real; lo atenuado quedará recortado.</p>
            </div>
          )}
          {!isDesktop && view === "editor" && <div data-testid="step-adjust" className="mt-5 border-t border-[#EAE7E2] pt-5">{adjust}</div>}
        </div>
      </div>

      <div data-testid="controls-column" className="min-w-0 space-y-7">
        <div>
          <h1 className="font-serif-display text-[32px] font-medium leading-tight sm:text-[42px]">Personaliza tu LUMINA</h1>
          <p className="mt-2 text-base text-[#080808]/70">Elige el tamaño, la orientación y dale un lugar a tu foto.</p>
        </div>
        <section data-testid="step-size"><H3>Tamaño</H3>
          <SizeButtons products={products} value={state.slug} orientation={state.orientation} onChange={(s) => s !== state.slug && changeShape({ slug: s })} />
        </section>
        <section data-testid="step-orientation"><H3>Orientación</H3>
          <OrientationButtons value={state.orientation} onChange={(o) => o !== state.orientation && changeShape({ orientation: o })} />
        </section>
        <div className="flex items-baseline justify-between gap-3 border-y border-[#EAE7E2] py-4">
          <p data-testid="designer-dims" className="text-sm text-[#080808]/70">{product.size} · {wcm} × {hcm} cm</p>
          <p data-testid="designer-price" className="font-serif-display text-4xl font-medium text-[#B18A4B]">{formatCOP(product.price_cop)}</p>
        </div>
        <section data-testid="step-mode">
          <ModeToggle collage={collage} onChange={(v) => changeLayout(v ? (collage ? state.layout : 2) : 1)} />
          {collage && (
            <div data-testid="step-layout" className="mt-4">
              <LayoutPicker layouts={cfg.layouts.filter((l) => l > 1)} templates={cfg.templates} orientation={state.orientation} value={state.layout} onChange={changeLayout} />
            </div>
          )}
        </section>
        <section data-testid="step-photos">
          <PhotoTray cfg={cfg} photos={photoList} onUploaded={onUploaded} onPick={pick} inputRef={uploadRef} onOpen={() => { replaceTarget.current = null; }} label="Sube tus fotos" hint="Después podrás ajustar el encuadre."
            usedIds={new Set(state.cells.map((c) => c.photo_id))} />
        </section>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p data-testid="finish-badges" className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.12em] text-[#080808]/70">
            <SquareRoundCorner className="h-3.5 w-3.5 text-[#B18A4B]" /> Bordes redondeados <span className="text-[#080808]/30">·</span> <Layers className="h-3.5 w-3.5 text-[#B18A4B]" /> Efecto flotante
          </p>
          <Link to="/como-funciona" data-testid="how-install-link" className="group inline-flex items-center gap-1 text-sm font-medium text-[#B18A4B] hover:underline">
            Cómo se instala <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        {isDesktop && filled > 0 && (
          <section data-testid="step-adjust" className="border-t border-[#EAE7E2] pt-6"><H3>Edita cada espacio</H3>
            {view === "room" && <button type="button" data-testid="open-editor-button" onClick={() => setView("editor")} className="mb-4 text-xs font-semibold uppercase tracking-[0.12em] underline underline-offset-4 hover:text-[#B18A4B]">Abrir editor de collage</button>}
            {adjust}
          </section>
        )}
        <section data-testid="step-review" className="border-t border-[#EAE7E2] pt-6 space-y-4">
          {missing > 0 && filled > 0 && <p data-testid="missing-warning" className="text-sm text-[#8B1E1E]">Faltan {missing} foto(s). Completa los espacios marcados para continuar.</p>}
          {hasLow && <p data-testid="lowres-warning" className="text-sm text-[#8B1E1E]">Una o más fotos tienen baja resolución para este tamaño: la impresión puede verse poco nítida.</p>}
          <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="confirm-crop-checkbox" checked={cropOk} onCheckedChange={(v) => setCropOk(!!v)} className="mt-0.5" />
            He revisado las fotografías y los recortes de mi collage.</label>
          {hasLow && (
            <label className="flex items-start gap-3 text-sm"><Checkbox data-testid="confirm-lowres-checkbox" checked={lowOk} onCheckedChange={(v) => setLowOk(!!v)} className="mt-0.5" />
              Entiendo que hay fotos de baja resolución y deseo continuar.</label>
          )}
          <button type="button" data-testid="confirm-add-to-cart-button" onClick={confirmAndAdd} disabled={busy || !cropOk || (hasLow && !lowOk) || missing > 0}
            className="w-full rounded-full bg-[#080808] py-4 text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B] disabled:opacity-40">
            {busy ? "Guardando…" : inCart ? "Confirmar diseño y actualizar carrito" : "Confirmar diseño y agregar al carrito"}
          </button>
          <button type="button" data-testid="save-draft-button" onClick={() => d.save(state).then(() => toast.success("Borrador guardado")).catch((e) => toast.error(e.message))}
            className="w-full rounded-full border border-[#080808]/20 py-3 text-[0.72rem] font-semibold uppercase tracking-[0.14em] hover:border-[#080808]">
            Guardar borrador
          </button>
        </section>
      </div>

      <AlertDialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <AlertDialogContent data-testid="relayout-warning">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cambiar la plantilla?</AlertDialogTitle>
            <AlertDialogDescription>La nueva plantilla tiene menos espacios: {pending?.lost} foto(s) saldrán del diseño y seguirán disponibles en tu bandeja.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="relayout-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="relayout-confirm" onClick={() => { pending.apply(); setPending(null); setHint(true); }}>Continuar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export function LegacyCustomizerRedirect() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const o = params.get("orientacion");
  return <Navigate replace to={`/personalizar?talla=${slug}${o ? `&orientacion=${o}` : ""}`} />;
}

export default function CustomizerPage() {
  useSeo({ title: "Personaliza tu LUMINA | LUMINA STUDIO", noindex: true });
  const { data } = useCatalog();
  const { data: cfg } = useQuery({ queryKey: ["designer-config"], queryFn: () => api.get("/designer/config").then((r) => r.data) });
  const [params] = useSearchParams();
  const { isSuccess: session } = useQuery({ queryKey: ["designer-session"], queryFn: () => api.post("/designer/session").then((r) => r.data), staleTime: Infinity });
  if (!data || !cfg || !session) return <div className="min-h-[70vh]" />;
  return <Designer key={params.get("diseno") || "nuevo"} products={data.products.filter((p) => p.available)} cfg={cfg} />;
}
